import React from 'react'
import { Box, Link as MuiLink, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'

export interface AdvisoryPayload {
  message?: string
  action_required?: string
  target_kind?: string
  target_uuid?: string
}

export interface AdvisoryRow {
  payload: AdvisoryPayload
  /** Populated by backend when target_kind === 'funding_round'. */
  target_opportunity_uuid?: string | null
  /**
   * Human-readable target name resolved by the backend (_resolve_advisory_row);
   * shown instead of the raw uuid in the review queue (GH #1844).
   */
  target_name?: string | null
}

/**
 * Produce an admin href for the advisory's target.
 *
 * - funder        → /funders/<uuid>
 * - opportunity   → /opportunities/<uuid>
 * - funding_round → /opportunities/<target_opportunity_uuid>  (degraded if absent)
 */
export function advisoryTargetHref(
  target_kind: string | undefined,
  target_uuid: string | undefined,
  target_opportunity_uuid: string | null | undefined,
): string | null {
  if (!target_kind) return null
  if (target_kind === 'funder') {
    return target_uuid ? `/funders/${target_uuid}` : null
  }
  if (target_kind === 'opportunity') {
    return target_uuid ? `/opportunities/${target_uuid}` : null
  }
  if (target_kind === 'funding_round') {
    return target_opportunity_uuid
      ? `/opportunities/${target_opportunity_uuid}`
      : null
  }
  return null
}

interface AdvisoryDetailProps {
  item: AdvisoryRow
}

/**
 * Renders an advisory proposed edit: message (bold heading), action_required
 * (body text), and a link to the target resource.
 *
 * No field-by-field diff — advisory payloads are advisory messages, not
 * mutations.
 */
export const AdvisoryDetail: React.FC<AdvisoryDetailProps> = ({ item }) => {
  const { message, action_required, target_kind, target_uuid } = item.payload
  const href = advisoryTargetHref(
    target_kind,
    target_uuid,
    item.target_opportunity_uuid,
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {message && (
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {message}
          </Typography>
        </Box>
      )}

      {action_required && (
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            Action required
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {action_required}
          </Typography>
        </Box>
      )}

      {target_kind && (
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            Target ({target_kind})
          </Typography>
          {href ? (
            <MuiLink
              component={RouterLink}
              to={href}
              underline="hover"
            >
              {item.target_name ?? target_uuid ?? target_kind}
            </MuiLink>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {item.target_name ?? target_uuid ?? '(no link available)'}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )
}
